export const utahWaterData = {
  title: "Utah Water",
  subtitle:
    "A report on per-capita water use, landscape conversion savings, rebate activity, Great Salt Lake tributaries, and county conservation targets.",
  thesis: "Utah residential water use and Great Salt Lake conservation.",
  focusAreas: [
    "How much water is being used per capita",
    "When people switch from lawn to water-wise usage, how much water is saved",
    "Utah rebate activity tied to water-wise upgrades",
    "How residential water savings can positively affect Great Salt Lake",
    "County-level usage needed to measure progress",
  ],
  statewideUsageTrends: [
    { year: "2015", gpcd: 242, status: "Baseline Year" },
    { year: "2019", gpcd: 223, status: "Pre-Drought Low" },
    { year: "2020", gpcd: 256, status: "High (dry year + home use spike)" },
    { year: "2023", gpcd: 207, status: "Progress toward 2030 goal" },
    { year: "2030 Goal", gpcd: 202, status: "Target Efficiency" },
  ],
  sectorBreakdown: [
    {
      sector: "Agriculture",
      share: 75,
      useCase: "Crop irrigation and livestock",
    },
    {
      sector: "Municipal & Industrial",
      share: 25,
      useCase: "Residential, commercial, and institutional",
    },
  ],
  municipalResidentialBreakdown: [
    {
      label: "Outdoor Residential",
      shareLabel: "60%",
      shareValue: 60,
      description: "Primarily lawn irrigation",
    },
    {
      label: "Indoor Residential",
      shareLabel: "40%",
      shareValue: 40,
      description: "Showers, toilets, laundry",
    },
  ],
  landscapeShift: {
    intro:
      "Transitioning from a traditional turf lawn to water-wise landscaping is typically driven by economics and environmental necessity. Regional studies across the Intermountain West show consistent triggers and measurable savings.",
    switchFactorsIntro:
      "The switch usually happens when one or more of these forces become hard to ignore.",
    switchFactors: [
      {
        title: "Financial Incentives",
        description:
          'Municipal "Flip Your Strip" or "Cash for Grass" rebates usually pay $1.00 to $3.00 per square foot and reduce the upfront cost of conversion.',
      },
      {
        title: "Regulatory Pressure",
        description:
          "Tiered pricing and tighter irrigation schedules push homeowners away from high-maintenance turf.",
      },
      {
        title: "Cultural Shift",
        description:
          "Visible neighborhood adoption lowers the perceived risk and raises interest in modern water-wise landscapes.",
      },
    ],
    savingsTable: [
      {
        landscapeType: "Traditional Kentucky Bluegrass",
        annualWaterNeed: "25-35 gallons per sq. ft.",
        percentageSaved: "0% (baseline)",
      },
      {
        landscapeType: "Hybrid / Drought-Tolerant Turf",
        annualWaterNeed: "15-20 gallons per sq. ft.",
        percentageSaved: "30%",
      },
      {
        landscapeType: "Full Water-Wise / Xeriscape",
        annualWaterNeed: "5-10 gallons per sq. ft.",
        percentageSaved: "50% - 70%",
      },
    ],
    savingsMetrics: [
      { metric: "Lawn area replaced in 2024", value: "3 million sq. ft." },
      { metric: "Annual water saved in 2024", value: "100+ million gallons" },
      {
        metric: "Average savings per sq. ft. removed",
        value: "33 gallons per year",
      },
      {
        metric: "Typical lawn area per home",
        value: "2,000-3,000 sq. ft.",
      },
      {
        metric: "Typical annual savings per lot",
        value: "30,000-90,000 gallons",
      },
    ],
  },
  rebates: {
    source: "https://slowtheflow.org/rebates/",
    landscapeProgramTitle:
      "Landscape Incentive Program (commonly known as the turf buyout)",
    landscapeProgram: [
      {
        metric: "Total Rebates Distributed",
        actual2024: "$7.1 Million",
        estimate2025: "$8.5 Million",
      },
      {
        metric: "Lawn Replaced",
        actual2024: "3.1 Million sq. ft.",
        estimate2025: "3.8 Million sq. ft.",
      },
      {
        metric: "Annual Water Saved",
        actual2024: "100 Million Gallons",
        estimate2025: "125 Million Gallons",
      },
      {
        metric: "Rebate Rate",
        actual2024: "$1.00 - $3.00 per sq. ft.",
        estimate2025: "$1.50 - $3.00 per sq. ft.",
      },
    ],
    nonLandscapePrograms: [
      {
        category: "Smart Irrigation Controllers",
        incentive: "Up to $100",
        impact:
          "Uses local weather data to skip watering on rainy days.",
      },
      {
        category: "Toilet Replacement",
        incentive: "Up to $150",
        impact: "For replacing pre-1994 toilets; saves 5 gallons per flush.",
      },
      {
        category: "Treebates",
        incentive: "$50 - $100 per tree",
        impact: "Encourages shade trees that reduce lawn evaporation.",
      },
      {
        category: "Drip Conversion",
        incentive: "$0.50 - $1.00 per sq. ft.",
        impact: "Moves garden beds from spray heads to targeted drip lines.",
      },
    ],
  },
  greatSaltLake: {
    intro:
      "Most water reaching Great Salt Lake comes from three river basins. Because the lake is terminal, every conserved acre-foot upstream directly matters to lake elevation and wetland health.",
    tributaries: [
      {
        tributary: "Bear River",
        inflowPercent: 60,
        snowpack: "79% - 104%",
        annualVolumeAf: 1200000,
        currentFlow: "880 cfs",
        conservationBufferAf: 45000,
        entryPoint: "Bear River Bay (North/South Arm)",
      },
      {
        tributary: "Weber River",
        inflowPercent: 20,
        snowpack: "49% - 71%",
        annualVolumeAf: 400000,
        currentFlow: "50 cfs",
        conservationBufferAf: 22000,
        entryPoint: "Ogden Bay / Fremont Island",
      },
      {
        tributary: "Jordan River",
        inflowPercent: 15,
        snowpack: "43% - 68%",
        annualVolumeAf: 300000,
        currentFlow: "150 cfs",
        conservationBufferAf: 18000,
        entryPoint: "Farmington Bay / South Arm",
      },
      {
        tributary: "Other Minor Inflows",
        inflowPercent: 5,
        snowpack: "-",
        annualVolumeAf: 100000,
        currentFlow: "Varies",
        conservationBufferAf: null,
        entryPoint: "Goggin Drain, Lee Creek, and others",
      },
    ],
    strategicImportance: [
      {
        title: "The Bear River (The Giant)",
        description:
          "The Bear River is the largest contributor by far. Conservation in Box Elder County affects flows into the Bear River Migratory Bird Refuge before water reaches the open lake.",
      },
      {
        title: "The Weber River (The Connector)",
        description:
          "Water saved in Ogden and Layton stays in the Weber system and supports the brackish marshes used by millions of migratory shorebirds.",
      },
      {
        title: "The Jordan River (The Urban Link)",
        description:
          "Water moving from Utah Lake through Salt Lake Valley returns through the Jordan and enters Farmington Bay, a key nursery zone for the lake ecosystem.",
      },
    ],
  },
  countyUsage: {
    source:
      "https://waterdesk.org/2025/12/utah-has-county-by-county-water-goals-which-ones-are-hitting-them/",
    counties: [
      { county: "Beaver", avg2015to2019: 688, avg2020to2024: 548, current2024: 456, goal2030: 390 },
      { county: "Box Elder", avg2015to2019: 355, avg2020to2024: 339, current2024: 350, goal2030: 266 },
      { county: "Cache", avg2015to2019: 275, avg2020to2024: 285, current2024: 304, goal2030: 233 },
      { county: "Carbon", avg2015to2019: 240, avg2020to2024: 197, current2024: 197, goal2030: 239 },
      { county: "Daggett", avg2015to2019: 550, avg2020to2024: 337, current2024: 259, goal2030: 343 },
      { county: "Davis", avg2015to2019: 247, avg2020to2024: 214, current2024: 228, goal2030: 188 },
      { county: "Duchesne", avg2015to2019: 375, avg2020to2024: 455, current2024: 458, goal2030: 267 },
      { county: "Emery", avg2015to2019: 562, avg2020to2024: 497, current2024: 535, goal2030: 376 },
      { county: "Garfield", avg2015to2019: 622, avg2020to2024: 582, current2024: 525, goal2030: 463 },
      { county: "Grand", avg2015to2019: 385, avg2020to2024: 313, current2024: 305, goal2030: 282 },
      { county: "Iron", avg2015to2019: 230, avg2020to2024: 186, current2024: 172, goal2030: 193 },
      { county: "Juab", avg2015to2019: 317, avg2020to2024: 314, current2024: 305, goal2030: 292 },
      { county: "Kane", avg2015to2019: 295, avg2020to2024: 319, current2024: 314, goal2030: 304 },
      { county: "Millard", avg2015to2019: 565, avg2020to2024: 490, current2024: 460, goal2030: 422 },
      { county: "Morgan", avg2015to2019: 217, avg2020to2024: 176, current2024: 183, goal2030: 187 },
      { county: "Piute", avg2015to2019: 557, avg2020to2024: 589, current2024: 564, goal2030: 341 },
      { county: "Rich", avg2015to2019: 992, avg2020to2024: 628, current2024: 451, goal2030: 984 },
      { county: "Salt Lake", avg2015to2019: 198, avg2020to2024: 180, current2024: 206, goal2030: 186 },
      { county: "San Juan", avg2015to2019: 234, avg2020to2024: 146, current2024: 110, goal2030: 180 },
      { county: "Sanpete", avg2015to2019: 348, avg2020to2024: 315, current2024: 252, goal2030: 272 },
      { county: "Sevier", avg2015to2019: 513, avg2020to2024: 371, current2024: 379, goal2030: 322 },
      { county: "Summit", avg2015to2019: 303, avg2020to2024: 243, current2024: 254, goal2030: 300 },
      { county: "Tooele", avg2015to2019: 217, avg2020to2024: 166, current2024: 180, goal2030: 195 },
      { county: "Uintah", avg2015to2019: 243, avg2020to2024: 219, current2024: 234, goal2030: 212 },
      { county: "Utah", avg2015to2019: 218, avg2020to2024: 200, current2024: 216, goal2030: 172 },
      { county: "Wasatch", avg2015to2019: 287, avg2020to2024: 281, current2024: 321, goal2030: 265 },
      { county: "Washington", avg2015to2019: 299, avg2020to2024: 266, current2024: 259, goal2030: 260 },
      { county: "Wayne", avg2015to2019: 725, avg2020to2024: 1156, current2024: 969, goal2030: 412 },
      { county: "Weber", avg2015to2019: 262, avg2020to2024: 273, current2024: 234, goal2030: 202 },
    ],
  },
  sources: [
    "https://slowtheflow.org/rebates/",
    "https://waterdesk.org/2025/12/utah-has-county-by-county-water-goals-which-ones-are-hitting-them/",
    "https://water.utah.gov",
    "https://waterrights.utah.gov/",
    "https://www.utahfoundation.org/uploads/rr723.pdf",
  ],
};

export default utahWaterData;
